/**
 * Where a signed-in user belongs, by role.
 *
 * Lives here rather than in App so both the "/" redirect and the post-sign-in
 * redirect can share it without App and Login importing each other.
 *
 * Every authenticated user used to land on the public catalog, which meant a
 * teacher pressing "Inicio" arrived at a page of other teachers' courses
 * offering to enrol them — the behaviour QA reported.
 */
export const homePathForRole = (role?: string): string => {
  switch (role) {
    case 'TEACHER':
      return '/teacher/dashboard';
    case 'PARENT':
      return '/parent/dashboard';
    case 'ADMIN':
    case 'MODERATOR':
      return '/admin/dashboard/ops';
    default:
      return '/courses';
  }
};

/**
 * Screens a returning user must never be sent back to: they would sign in and
 * arrive at the form they just filled in.
 */
const AUTH_PATHS = [
  '/auth',
  '/login',
  '/register',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
];

/**
 * Where to go once sign-in succeeds (PRT-BR-P01-03, prototype defect PG-04).
 *
 * A parent who presses "Inscribirse" on a course before signing in is sent to
 * /login; returning them to their dashboard afterwards throws away the course
 * they chose. So the guard records where they were going and this resumes it —
 * but only to a path inside this app. The target arrives through router state,
 * which anything that can link here can set, so an absolute URL, a
 * protocol-relative `//host` or a backslash variant falls back to the role's
 * home rather than becoming an open redirect.
 */
export const returnPathAfterSignIn = (from: unknown, role?: string): string => {
  if (typeof from !== 'string' || !from.startsWith('/')) return homePathForRole(role);
  if (from.startsWith('//') || from.includes('\\')) return homePathForRole(role);
  const pathname = from.split(/[?#]/)[0];
  if (AUTH_PATHS.includes(pathname)) return homePathForRole(role);
  return from;
};
